import { loader } from '@monaco-editor/react';
import { preloadMonacoEditor } from 'js/components/elements/editors/monaco/LoadableMonacoEditor';

// Worker files to preload (optional - provides ~50-200ms faster first language service)
const MONACO_WORKERS = [
  '/monaco-workers/editor.worker.js',
  '/monaco-workers/json.worker.js',
  // ts.worker.js is large (11MB) - only preload if TypeScript editing is common
  // '/monaco-workers/ts.worker.js',
  '/monaco-workers/css.worker.js',
  '/monaco-workers/html.worker.js',
];

let preloadPromise: Promise<void> | null = null;

/**
 * Preload Monaco worker files by fetching them into browser cache.
 * These are loaded on-demand by Monaco, so preloading is optional but
 * eliminates the small delay when a language service is first used.
 */
const preloadWorkers = () => {
  return Promise.all(
    MONACO_WORKERS.map((workerPath) => fetch(workerPath, { cache: 'force-cache' }).catch(() => {})),
  );
};

/**
 * Preload Monaco editor, components, and workers in the background.
 * Safe to call multiple times - returns the same promise.
 *
 * Uses requestIdleCallback to avoid blocking the main thread
 * during initial app render.
 */
export const preloadMonaco = (): Promise<void> => {
  // Return existing promise if preload already started
  if (preloadPromise) {
    return preloadPromise;
  }

  preloadPromise = new Promise((resolve) => {
    const doPreload = async () => {
      try {
        // Run all preloads in parallel
        await Promise.all([
          // 1. Preload worker files into browser cache
          preloadWorkers(),

          // 2. Preload the lazy-loaded MonacoCodeEditor component
          // Uses the SAME import function as LoadableMonacoEditor
          preloadMonacoEditor(),

          // 3. Initialize Monaco loader (idempotent)
          loader.init().catch(() => {}),
        ]);
      } catch (err) {
        // Silently ignore preload errors
        console.debug('Monaco preload error:', err);
      }
      resolve();
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(doPreload, { timeout: 3000 });
    } else {
      setTimeout(doPreload, 1000);
    }
  });

  return preloadPromise;
};

export default preloadMonaco;
